/**
 * PartsSection - Products and custom parts management
 *
 * Combines Hajoca products and custom parts in one unified section
 */

import { useTranslation } from "react-i18next";
import React, { useState, useCallback, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { ProductSelectionButton } from "@/components/products/ProductSelectionButton";
import { SelectedProductsList } from "@/components/products/SelectedProductsList";
import {
  SelectedCustomPartsList,
  SelectedCustomPart,
} from "@/components/custom-parts/SelectedCustomPartsList";
import { CustomPartDialog } from "@/components/custom-parts/CustomPartDialog";
import { QuoteFormData, TemplateFormData } from "../index";
import type { QuoteProduct, CustomPart } from "@onsemble/shared";
import { Product } from "@/types/product";
import { PricingService } from "@shared/pricing-service";

interface PartsSectionProps {
  form: UseFormReturn<QuoteFormData | TemplateFormData>;
  organizationId: string;
  products?: Product[];
  productsLoading?: boolean;
  isSubmitting?: boolean;
}

export const PartsSection: React.FC<PartsSectionProps> = ({
  form,
  organizationId,
  products = [],
  productsLoading = false,
  isSubmitting = false,
}) => {
  const { t } = useTranslation();
  const [isAddingCustomPart, setIsAddingCustomPart] = useState(false);

  // Memoized handlers for products
  const handleProductsChange = useCallback(
    (products: QuoteProduct[]) => {
      form.setValue("products", products, {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    [form],
  );

  const handleProductQuantityChange = useCallback(
    (productId: string, quantity: number) => {
      const currentProducts = form.watch("products") || [];
      if (quantity === 0) {
        form.setValue(
          "products",
          currentProducts.filter((p) => p.productId !== productId),
          {
            shouldValidate: true,
            shouldDirty: true,
          },
        );
      } else {
        form.setValue(
          "products",
          currentProducts.map((p) =>
            p.productId === productId ? { ...p, quantity } : p,
          ),
          { shouldValidate: true, shouldDirty: true },
        );
      }
    },
    [form],
  );

  const handleProductRemove = useCallback(
    (productId: string) => {
      const currentProducts = form.watch("products") || [];
      form.setValue(
        "products",
        currentProducts.filter((p) => p.productId !== productId),
        {
          shouldValidate: true,
          shouldDirty: true,
        },
      );
    },
    [form],
  );

  const handleClearAllProducts = useCallback(() => {
    form.setValue("products", [], { shouldValidate: true, shouldDirty: true });
  }, [form]);

  const handleAddProduct = useCallback(
    (product: Product, quantity: number) => {
      const currentProducts = (form.watch("products") || []) as QuoteProduct[];
      const existing = currentProducts.find(
        (p) => p.productId === product.uid,
      );
      if (existing) {
        form.setValue(
          "products",
          currentProducts.map((p) =>
            p.productId === product.uid
              ? { ...p, quantity: p.quantity + quantity }
              : p,
          ),
          { shouldValidate: true, shouldDirty: true },
        );
      } else {
        const newProduct: QuoteProduct = {
          productId: product.uid,
          quantity,
          notes: "",
        };
        form.setValue("products", [...currentProducts, newProduct], {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    },
    [form],
  );

  // Memoized handlers for custom parts
  const handleCustomPartsChange = useCallback(
    (customParts: SelectedCustomPart[]) => {
      form.setValue("quoteCustomParts", customParts, {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    [form],
  );

  const handleCustomPartQuantityChange = useCallback(
    (customPartId: string, quantity: number) => {
      const current = (form.watch("quoteCustomParts") ||
        []) as SelectedCustomPart[];
      const updated = current.map((cp) =>
        cp.customPartId === customPartId ? { ...cp, quantity } : cp,
      );
      handleCustomPartsChange(updated);
    },
    [form, handleCustomPartsChange],
  );

  const handleCustomPartRemove = useCallback(
    (customPartId: string) => {
      const current = (form.watch("quoteCustomParts") ||
        []) as SelectedCustomPart[];
      const updated = current.filter((cp) => cp.customPartId !== customPartId);
      handleCustomPartsChange(updated);
    },
    [form, handleCustomPartsChange],
  );

  const handleClearAllCustomParts = useCallback(() => {
    handleCustomPartsChange([]);
  }, [handleCustomPartsChange]);

  const handleCustomPartSelect = useCallback(
    (selectedCustomPart: CustomPart) => {
      const currentSelectedData = (form.watch("quoteCustomParts") ||
        []) as SelectedCustomPart[];

      const isAlreadySelected = currentSelectedData.some(
        (cp) => cp.customPartId === selectedCustomPart.uid,
      );

      if (isAlreadySelected) {
        // Remove from selected custom parts
        const newSelectedData = currentSelectedData.filter(
          (cp) => cp.customPartId !== selectedCustomPart.uid,
        );
        handleCustomPartsChange(newSelectedData);
      } else {
        // Add to selected custom parts - safely access properties with fallbacks
        const newCustomPart: SelectedCustomPart = {
          customPartId: selectedCustomPart.uid,
          quantity: 1,
          customPrice: selectedCustomPart.cost ?? 0,
          customName: selectedCustomPart.title ?? "Unknown Custom Part",
        };
        handleCustomPartsChange([...currentSelectedData, newCustomPart]);
      }
    },
    [form, handleCustomPartsChange],
  );

  // Enrich products with pricing data (including markup) for display
  // This follows the same pattern as QuoteViewer to ensure consistent pricing display
  // Note: products prop now contains all selected products (merged in QuoteEditor)
  const enrichedProducts = useMemo(() => {
    const selectedProducts = form.watch("products") || [];
    const markupPercent = form.watch("markupPercent") || 0;
    const partsAffectPrice = form.watch("partsAffectPrice") ?? true;

    // If no products selected, return empty
    if (selectedProducts.length === 0) {
      return selectedProducts;
    }

    // If products still loading, return bare products temporarily
    if (productsLoading || products.length === 0) {
      return selectedProducts;
    }

    // Use PricingService to calculate prices with markup applied
    const { products: enhanced } = PricingService.calculateProductPricing(
      selectedProducts,
      products,
      markupPercent,
      partsAffectPrice,
    );

    // Transform to SelectedProductsList format (add unitPrice field)
    // SelectedProductsList expects products with unitPrice to display pricing
    return enhanced.map((p) => {
      const originalProduct = selectedProducts.find(
        (sp) => sp.productId === p.productId,
      );
      return {
        productId: p.productId,
        quantity: p.quantity,
        notes: originalProduct?.notes,
        unitPrice: p.postMarkupUnitPrice, // Add unitPrice for pricing display
      };
    });
  }, [
    form.watch("products"),
    form.watch("markupPercent"),
    form.watch("partsAffectPrice"),
    products,
    productsLoading,
  ]);

  return (
    <div className="border-t pt-6">
      <div className="space-y-6">
        {/* Hajoca Products */}
        <div>
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              {t("features.quotes.quoteEditor.sections.partsSection.partsMaterials")}
            </h3>
            <ProductSelectionButton
              selectedProducts={form.watch("products") || []}
              onProductsChange={handleProductsChange}
              showPricing={true}
              disabled={isSubmitting}
              triggerText={t("features.quotes.quoteEditor.sections.partsSection.selectHajocaProducts")}
              modalTitle={t("features.quotes.quoteEditor.sections.partsSection.productCatalog")}
              modalSize="sm"
            />
          </div>

          <SelectedProductsList
            selectedProducts={enrichedProducts}
            onProductQuantityChange={handleProductQuantityChange}
            onProductRemove={handleProductRemove}
            onClearAll={handleClearAllProducts}
            onAddProduct={handleAddProduct}
            showPricing={true}
            disabled={isSubmitting}
            title={t("features.quotes.quoteEditor.sections.partsSection.selectedProducts")}
            products={products}
            productsLoading={productsLoading}
          />
        </div>

        {/* Custom Parts */}
        <div>
          <SelectedCustomPartsList
            selectedCustomParts={
              (form.watch("quoteCustomParts") || []) as SelectedCustomPart[]
            }
            onCustomPartsChange={handleCustomPartsChange}
            onCustomPartQuantityChange={handleCustomPartQuantityChange}
            onCustomPartRemove={handleCustomPartRemove}
            onClearAll={handleClearAllCustomParts}
            disabled={isSubmitting}
            orgId={organizationId}
            onCustomPartSelect={handleCustomPartSelect}
            onAddNewCustomPart={() => setIsAddingCustomPart(true)}
          />
        </div>
      </div>

      {/* Custom Part Dialog */}
      <CustomPartDialog
        isOpen={isAddingCustomPart}
        onClose={() => setIsAddingCustomPart(false)}
        mode="add"
        orgId={organizationId}
        onCustomPartAdded={(customPartId, customPart) => {
          setIsAddingCustomPart(false);
          if (customPart) {
            // Add the new custom part to the selected list
            const currentSelectedData = (form.watch("quoteCustomParts") ||
              []) as SelectedCustomPart[];
            const newCustomPart: SelectedCustomPart = {
              customPartId: customPartId,
              quantity: 1,
              customPrice: customPart.cost ?? 0,
              customName: customPart.title ?? "Unknown Custom Part",
            };
            handleCustomPartsChange([...currentSelectedData, newCustomPart]);
          }
        }}
      />
    </div>
  );
};
